import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Check, X, Trash2, AlertTriangle } from "lucide-react";

const TYPES = [
  { v: "ferie", l: "Ferie" }, { v: "permesso", l: "Permesso" }, { v: "malattia", l: "Malattia" },
  { v: "assenza", l: "Assenza" }, { v: "giorno_libero", l: "Giorno libero" },
  { v: "formazione", l: "Formazione" }, { v: "altro", l: "Altro" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));
const STATUS_STYLE = {
  pending: "text-[hsl(var(--hi-status-info))] bg-[hsl(var(--hi-status-info)/0.12)]",
  approved: "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)]",
  rejected: "text-destructive bg-destructive/10",
};
const STATUS_LABEL = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" };

export function AbsencesTab({ team, canWrite }) {
  const staff = team.filter((t) => ["stylist", "assistant", "manager", "reception"].includes(t.role));
  const nameById = Object.fromEntries(team.map((t) => [t.id, t.name]));
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ operator_id: staff[0]?.id || "", type: "ferie", start: "", end: "", full_day: true });
  const [conflicts, setConflicts] = useState([]);

  const load = useCallback(async () => {
    const q = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    try { const { data } = await api.get(`/team/absences${q}`); setItems(data); }
    catch (e) { setItems([]); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.start || !form.end) { toast.error("Inserisci le date"); return; }
    try {
      const payload = { ...form, start: form.start + "T00:00:00+00:00", end: form.end + "T23:59:59+00:00" };
      const { data } = await api.post("/team/absences", payload);
      setConflicts(data.conflicts || []);
      if ((data.conflicts || []).length) toast.warning(`${data.conflicts.length} appuntamenti in conflitto: gestiscili in Agenda`);
      else toast.success("Assenza registrata");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const decide = async (id, decision) => {
    try { const { data } = await api.post(`/team/absences/${id}/decision`, { decision });
      if (decision === "approved" && (data.conflicts || []).length) { setConflicts(data.conflicts); toast.warning(`${data.conflicts.length} appuntamenti in conflitto`); }
      else toast.success(decision === "approved" ? "Approvata" : "Rifiutata");
      load(); }
    catch (e) { toast.error("Errore"); }
  };
  const del = async (id) => { try { await api.delete(`/team/absences/${id}`); load(); } catch (e) { toast.error("Errore"); } };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="absence-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="pending">In attesa</SelectItem><SelectItem value="approved">Approvate</SelectItem><SelectItem value="rejected">Rifiutate</SelectItem></SelectContent>
        </Select>
        {canWrite && <Button onClick={() => { setForm({ operator_id: staff[0]?.id || "", type: "ferie", start: "", end: "", full_day: true }); setOpen(true); }} data-testid="absence-new"><Plus className="h-4 w-4 mr-1" /> Nuova assenza</Button>}
      </div>

      {conflicts.length > 0 && (
        <Card className="p-3 border-[hsl(var(--hi-status-warning,38_92%_50%)/0.4)] bg-[hsl(38_92%_50%/0.08)]" data-testid="conflicts-box">
          <div className="flex items-center gap-2 font-medium text-sm mb-1"><AlertTriangle className="h-4 w-4 text-[hsl(38_92%_45%)]" /> Appuntamenti in conflitto con l'assenza</div>
          {conflicts.map((c) => <div key={c.id} className="text-xs text-muted-foreground">{fmtDateTime(c.start)} · {c.client_name} · {(c.services || []).join(", ")}</div>)}
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setConflicts([])}>Ho capito</Button>
        </Card>
      )}

      {items === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> : items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="absences-empty">Nessuna assenza.</Card>
      ) : (
        <div className="grid gap-2">
          {items.map((a) => (
            <Card key={a.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`absence-${a.id}`}>
              <div className="flex-1 min-w-[160px]">
                <div className="font-medium text-sm">{nameById[a.operator_id] || "—"} · {TYPE_LABEL[a.type] || a.type}</div>
                <div className="text-xs text-muted-foreground">{(a.start || "").slice(0, 10)} → {(a.end || "").slice(0, 10)}{a.note_staff ? ` · ${a.note_staff}` : ""}</div>
              </div>
              <Badge className={`text-[10px] ${STATUS_STYLE[a.status] || ""}`}>{STATUS_LABEL[a.status] || a.status}</Badge>
              {canWrite && a.status === "pending" && <>
                <Button size="sm" variant="ghost" className="text-[hsl(var(--hi-status-success))]" onClick={() => decide(a.id, "approved")} data-testid={`absence-approve-${a.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => decide(a.id, "rejected")} data-testid={`absence-reject-${a.id}`}><X className="h-4 w-4" /></Button>
              </>}
              {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Nuova assenza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Collaboratore</Label><Select value={form.operator_id} onValueChange={(v) => setForm({ ...form, operator_id: v })}><SelectTrigger data-testid="absence-operator"><SelectValue /></SelectTrigger><SelectContent>{staff.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Tipo</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="absence-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="absence-start" /></div>
              <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="absence-end" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.full_day} onCheckedChange={(v) => setForm({ ...form, full_day: v })} /><Label>Giornata intera</Label></div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="absence-save">Registra</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
